/**
 * Lightweight router — Sprint 1 only. We'll graduate to TanStack Router in
 * Sprint 11 once we have multiple authenticated pages worth modeling.
 *
 * Routes:
 *   /invite/:token  -> ActivatePage (sets first password / resets password)
 *   /              -> LoginPage if not authenticated, DashboardPage if authenticated
 */

import { useEffect, useState } from "react";
import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { CompanyShell } from "@/components/CompanyShell";
import { Toaster, errorToastShownRecently, pushToast } from "@/components/Toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ApiError } from "@/lib/api";
import { useCurrentUser } from "@/lib/auth";
import { AccountsPage } from "@/pages/AccountsPage";
import { ActivatePage } from "@/pages/ActivatePage";
import { AggregatorPage } from "@/pages/AggregatorPage";
import { BroadcastsPage } from "@/pages/BroadcastsPage";
import { CompaniesPage } from "@/pages/admin/CompaniesPage";
import { ChannelsPage } from "@/pages/ChannelsPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { InboxPage } from "@/pages/InboxPage";
import { InviterPage } from "@/pages/InviterPage";
import { LoginPage } from "@/pages/LoginPage";
import { OwnChatsPage } from "@/pages/OwnChatsPage";
import { ProxiesPage } from "@/pages/ProxiesPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { Setup2faPage } from "@/pages/Setup2faPage";
import { TeamPage } from "@/pages/TeamPage";
import { UsersPage } from "@/pages/UsersPage";

const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    // Safety net for actions whose caller shows nothing on failure — without it
    // the button just stops spinning. A caller that does report the error reads
    // `error.detail` in its catch (which runs before this timeout) and so marks
    // it as shown; one that reports it in its own words has just raised an
    // error toast. Background mutations opt out with `meta.silentErrors`.
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.meta?.silentErrors) return;
      setTimeout(() => {
        if (error instanceof ApiError && !error.shown && !errorToastShownRecently()) {
          pushToast({ kind: "error", text: error.detail });
        }
      }, 0);
    },
  }),
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

type Route =
  | { name: "home" }
  | { name: "activate"; token: string }
  | { name: "setup_2fa" }
  | { name: "team" }
  | { name: "settings" }
  | { name: "proxies" }
  | { name: "accounts" }
  | { name: "channels" }
  | { name: "users" }
  | { name: "inviter" }
  | { name: "broadcasts" }
  | { name: "aggregator" }
  | { name: "own_chats" }
  | { name: "inbox" };

function parseRoute(): Route {
  const path = window.location.pathname;
  const inviteMatch = /^\/invite\/([A-Za-z0-9_-]{20,})$/.exec(path);
  if (inviteMatch) return { name: "activate", token: inviteMatch[1] };
  if (path === "/setup-2fa") return { name: "setup_2fa" };
  if (path === "/team") return { name: "team" };
  if (path === "/settings") return { name: "settings" };
  // Legacy paths — redirect to /settings with the right tab anchor.
  // /credentials is fully retired in the UI; legacy bookmarks land on /settings.
  if (path === "/credentials" || path === "/throttle") {
    const anchor = path === "/throttle" ? "#throttle" : "";
    window.history.replaceState(null, "", `/settings${anchor}`);
    return { name: "settings" };
  }
  if (path === "/proxies") return { name: "proxies" };
  if (path === "/accounts") return { name: "accounts" };
  if (path === "/channels") return { name: "channels" };
  if (path === "/users") return { name: "users" };
  if (path === "/inviter") return { name: "inviter" };
  if (path === "/broadcasts") return { name: "broadcasts" };
  if (path === "/aggregator") return { name: "aggregator" };
  if (path === "/own-chats") return { name: "own_chats" };
  if (path === "/inbox") return { name: "inbox" };
  return { name: "home" };
}

function navigate(to: string) {
  window.history.pushState(null, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function Routes() {
  const [route, setRoute] = useState(parseRoute);
  useEffect(() => {
    const update = () => setRoute(parseRoute());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  const { data: user, isLoading } = useCurrentUser();

  if (isLoading) {
    return (
      <main className="min-h-dvh flex items-center justify-center bg-bg-primary text-fg-muted">
        Loading…
      </main>
    );
  }

  if (route.name === "activate") {
    return <ActivatePage token={route.token} onSuccess={() => navigate("/")} />;
  }

  if (route.name === "setup_2fa") {
    return <Setup2faPage onSuccess={() => navigate("/")} />;
  }

  if (!user) {
    return (
      <LoginPage
        onSuccess={() => navigate("/")}
        onRequiresSetup={() => navigate("/setup-2fa")}
      />
    );
  }

  // Superadmin without active impersonation lands on the platform admin shell.
  // With an impersonation context, drop into the company shell (so they see
  // the product as the customer would) and surface the red banner.
  if (user.is_superadmin && !user.impersonating) {
    return <CompaniesPage />;
  }

  // Company users (and impersonating superadmins) get the shell layout.
  const innerKey =
    route.name === "team"
      ? "team"
      : route.name === "settings"
        ? "settings"
        : route.name === "proxies"
          ? "proxies"
          : route.name === "accounts"
            ? "accounts"
            : route.name === "channels"
              ? "channels"
              : route.name === "users"
                ? "users"
                : route.name === "inviter"
                  ? "inviter"
                  : route.name === "broadcasts"
                    ? "broadcasts"
                    : route.name === "aggregator"
                      ? "aggregator"
                      : route.name === "own_chats"
                        ? "own_chats"
                        : route.name === "inbox"
                          ? "inbox"
                          : "dashboard";

  let inner;
  switch (route.name) {
    case "team":
      inner = <TeamPage />;
      break;
    case "settings":
      inner = <SettingsPage />;
      break;
    case "proxies":
      inner = <ProxiesPage />;
      break;
    case "accounts":
      inner = <AccountsPage />;
      break;
    case "channels":
      inner = <ChannelsPage />;
      break;
    case "users":
      inner = <UsersPage />;
      break;
    case "inviter":
      inner = <InviterPage />;
      break;
    case "broadcasts":
      inner = <BroadcastsPage />;
      break;
    case "aggregator":
      inner = <AggregatorPage />;
      break;
    case "own_chats":
      inner = <OwnChatsPage />;
      break;
    case "inbox":
      inner = <InboxPage />;
      break;
    default:
      inner = <DashboardPage />;
  }

  return (
    <CompanyShell current={innerKey} onNavigate={navigate}>
      {inner}
    </CompanyShell>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <Routes />
      </TooltipProvider>
      <Toaster />
    </QueryClientProvider>
  );
}
